import { forwardRef, Ref, useEffect, useState } from 'react';

/**
 * @description Converts a number to a fixed number of decimal places without rounding.
 * Truncates the number at the specified decimal place using regex pattern matching.
 *
 * @SADtrace SAD-003-3.3, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {number} n - The number to convert
 * @param {number} fixed - The number of decimal places to keep
 *
 * @returns {number} The number with the specified decimal places (truncated, not rounded)
 */
const toFixedWithNoRounding = (n: number, fixed: number): number =>
  +(`${n}`.match(new RegExp(`^-?\\d+(?:.\\d{0,${fixed}})?`)) as string[])[0];

/**
 * @description Converts a number to a fixed number of decimal places with rounding.
 * Uses scientific notation to perform accurate rounding.
 *
 * @SADtrace SAD-003-3.3, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {number} n - The number to convert
 * @param {number} fixed - The number of decimal places to keep
 *
 * @returns {string} The number with the specified decimal places (rounded)
 */
const toFixedWhenThereIsRounding = (n: number, fixed: number) => {
  return Number(Math.round(+(n + 'e' + fixed)) + 'e' + -fixed).toFixed(fixed);
};

/** toNearestWholeNumber: if user inputs decimals onBlur, we round to whole number */
type RoundingType =
  | 'none'
  | 'toNearestWholeNumber'
  | 'toAllowedNumberOfDecimals';

/**
 * @description Props for the DecimalInput component
 * @typedef {Object} DecimalInputProps
 * @property {boolean} [disabled] - Whether the input is disabled
 * @property {Function} [onBlur] - Function to call when the input loses focus
 * @property {number} [precision] - Number of decimal places allowed
 * @property {boolean} [alignCenter] - Whether to center the text in the input
 * @property {string} [value] - The current value of the input
 * @property {Function} onChange - Function to call when the input value changes
 * @property {string} [className] - Additional CSS classes to apply
 * @property {string} [placeholder] - Placeholder text for the input
 * @property {RoundingType} [rounding] - Type of rounding to apply
 */
type DecimalInputProps = {
  disabled?: boolean;
  onBlur?: Function;
  precision?: number;
  alignCenter?: boolean;
  value?: string | undefined;
  onChange: Function;
  className?: string;
  placeholder?: string;
  rounding?: RoundingType;
};

/**
 * @description List of keys that are allowed to be pressed in the input
 */
const allowedKeys = [
  'Backspace',
  'Tab',
  'Escape',
  'ArrowLeft',
  'ArrowUp',
  'ArrowRight',
  'ArrowDown',
  'Delete',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '.',
];

/**
 * @description Counts the number of decimal places in a value
 *
 * @SADtrace SAD-003-3.3, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {string|number} value - The value to count decimal places for
 *
 * @returns {number} The number of decimal places
 */
const countDecimals = (value: string | number) => {
  // v8 ignore start -- WAIVER(DEFENSIVE): both call sites gate on hasDecimal, so the value always contains a '.'; the empty-string and no-dot arms are unreachable fail-safes kept for safe reuse
  if (value === '') return 0;
  return String(value).split('.').length > 1
    ? String(value).split('.')[1].length
    : 0;
  // v8 ignore stop -- WAIVER(DEFENSIVE)
};

/**
 * @description Checks if a value has only zeros after the decimal point
 *
 * @SADtrace SAD-003-3.3, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {string} value - The value to check
 *
 * @returns {boolean} True if the value has only zeros after the decimal point
 */
function hasOnlyZeroDecimals(value: string) {
  return /^\d+(\.0+)?$/.test(value);
}

/**
 * @description If a number has only zeros after the decimal point, returns just the whole number part
 *
 * @SADtrace SAD-003-3.3, SAD-003-4.3
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @param {string} number - The number to check
 *
 * @returns {string} The whole number part if applicable, otherwise the original number
 */
function ifNumberHasOnlyZeroDecimalsReturnWholePart(number: string) {
  //if value contains only 0's on the decimal part, we just leave the whole number
  if (hasOnlyZeroDecimals(number)) {
    const wholeNumber = number.split('.')[0];
    return wholeNumber;
  }
  return number;
}

/**
 * @description A customizable input component for decimal numbers with various formatting options
 *
 * @component
 * @example
 * // Basic usage with 2 decimal places
 * <DecimalInput
 *   value="10.5"
 *   onChange={(value) => console.log(value)}
 *   precision={2}
 * />
 *
 * @example
 * // With rounding to nearest whole number on blur
 * <DecimalInput
 *   value="10.5"
 *   onChange={(value) => console.log(value)}
 *   rounding="toNearestWholeNumber"
 * />
 */
// eslint-disable-next-line react/display-name
const DecimalInput = forwardRef(
  (
    {
      value,
      onChange,
      onBlur,
      disabled = false,
      precision = 0,
      className = '',
      placeholder = '',
      rounding = 'none',
      alignCenter = true,
      ...rest
    }: DecimalInputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const [stateValue, setStateValue] = useState<string | undefined>('');
    const [numberPrecision, setNumberPrecision] = useState<number>(precision);

    useEffect(() => {
      setNumberPrecision(precision);
    }, [precision]);

    useEffect(() => {
      if (!isNaN(value as any)) {
        setStateValue(value);
      } else if (value === '.') {
        setStateValue('.');
      } else {
        setStateValue('');
      }
    }, [value]);

    /**
     * @description Used when there is rounding and you can enter more decimals than precision.
     * Handles formatting for inputs that allow overtyping beyond precision limits.
     *
     * @SADtrace SAD-003-3.3, SAD-003-4.3
     *
     * @History
     * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
     * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
     *
     * @param {string} value - The current input value
     * @param {boolean} hasDecimal - Whether the value contains a decimal point
     *
     * @returns {string|number} The formatted value
     */
    const getRoundedFormattedValue = (value: string, hasDecimal: boolean) => {
      // turn .9 into 0.9
      if (/^\.\d$/.test(value)) {
        return `0${value}`;
      }

      if (value === '') return '';

      return hasDecimal ? value : Number(value);
    };

    /**
     * @description Used when there is no rounding and you cannot enter more decimals than precision.
     * Truncates values that exceed the allowed precision.
     *
     * @SADtrace SAD-003-3.3, SAD-003-4.3
     *
     * @History
     * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
     * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
     *
     * @param {string} value - The current input value
     * @param {number} numOfDec - Number of decimal places in the value
     * @param {number} precision - Maximum number of decimal places allowed
     * @param {boolean} hasDecimal - Whether the value contains a decimal point
     *
     * @returns {string|number} The formatted value
     */
    const getFormattedValue = (
      value: string,
      numOfDec: number,
      precision: number,
      hasDecimal: boolean,
    ) => {
      if (numOfDec > precision) {
        return toFixedWithNoRounding(Number(value), precision);
      }

      if (/^\.\d$/.test(value)) {
        return `0${value}`;
      }

      if (value === '') return '';

      return hasDecimal ? value : Number(value);
    };

    /**
     * @description Handles changes to the input value.
     * Applies appropriate formatting based on rounding settings and precision.
     *
     * @SADtrace SAD-003-3.3, SAD-003-4.3
     *
     * @History
     * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
     * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
     *
     * @param {React.ChangeEvent<HTMLInputElement>} event - The change event
     *
     * @returns {void}
     */
    const handleChange = (event: any) => {
      const value = event.target.value;

      if (String(value).split('.').length > 2) return;

      const hasDecimal = String(value).includes('.');
      const numOfDec = hasDecimal ? countDecimals(value) : 0;

      let floating;

      if (rounding !== 'none') {
        floating = getRoundedFormattedValue(value, hasDecimal);
      } else {
        floating = getFormattedValue(
          value,
          numOfDec,
          numberPrecision,
          hasDecimal,
        );
      }

      setStateValue(String(floating));
      onChange(String(floating));
    };

    /**
     * @description Prevents disallowed keys from being entered.
     * Allows only numeric keys, decimals, navigation keys, and select all shortcuts.
     *
     * @SADtrace SAD-003-3.3, SAD-003-4.3
     *
     * @History
     * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
     * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
     *
     * @param {React.KeyboardEvent<HTMLInputElement>} event - The keyboard event
     *
     * @returns {boolean} Whether the key is allowed
     */
    const handleKeyDown = (event: any) => {
      // Allow select all
      if (
        (event.metaKey && event.key.toLowerCase() === 'a') ||
        (event.ctrlKey && event.key.toLowerCase() === 'a')
      ) {
        return true;
      }

      if (rounding === 'none') {
        // For 0 decimals don't allow dot
        if (numberPrecision === 0 && event.key === '.') {
          event.preventDefault();
        }
      }

      if (!allowedKeys.includes(event.key)) {
        event.preventDefault();
      }
    };

    /**
     * @description Handles the blur event, applying formatting and rounding as needed.
     * Removes trailing decimals, applies precision rounding, and cleans up values.
     *
     * @SADtrace SAD-003-3.3, SAD-003-4.3
     *
     * @History
     * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
     * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
     *
     * @param {React.FocusEvent<HTMLInputElement>} event - The blur event
     *
     * @returns {void}
     */
    const handleBlur = (event: any) => {
      let value = String(event.target.value);

      if (value === '.') {
        setStateValue('');
        onChange('');
        //if value contains a decimal point but no decimals, we set it back to a whole number
      } else if (/^\d*\.$/.test(value)) {
        value = value.replace(/\.$/, '');
        // v8 ignore start -- WAIVER(DEFENSIVE): a bare '.' is consumed by the first blur branch, so the stripped value always keeps at least one digit; the empty-string arm is an unreachable fail-safe
        const newValue = value === '' ? '' : value;
        // v8 ignore stop -- WAIVER(DEFENSIVE)
        setStateValue(newValue);
        onChange(newValue);
      } else if (hasOnlyZeroDecimals(value)) {
        const wholeNumber = ifNumberHasOnlyZeroDecimalsReturnWholePart(value);
        setStateValue(wholeNumber);
        onChange(wholeNumber);
      }
      if (rounding === 'toAllowedNumberOfDecimals') {
        //if number of decimals is more than allowed we round the number
        const hasDecimal = String(value).includes('.');
        const numOfDec = hasDecimal ? countDecimals(value) : 0;

        if (numOfDec > precision) {
          let newValue = toFixedWhenThereIsRounding(Number(value), precision);

          newValue = ifNumberHasOnlyZeroDecimalsReturnWholePart(newValue);

          setStateValue(newValue + '');
          onChange(newValue);
        }
      }

      if (rounding === 'toNearestWholeNumber') {
        const newValue = Math.round(+value);
        setStateValue(newValue + '');
        onChange(newValue);
      }

      if (onBlur) {
        onBlur(event);
      }
    };

    return (
      <>
        <input
          ref={ref}
          onChange={handleChange}
          onBlur={handleBlur}
          value={stateValue}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={
            `decimal-input form-control form-control-lg rounded-1 ${alignCenter ? 'text-center' : ''} ` +
            className
          }
          {...rest}
        />
      </>
    );
  },
);

export default DecimalInput;
